import React, { useCallback, useState } from 'react';
import { Box, Button, Popover, Typography, useTheme } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { TipTapEditor } from '../../TipTap';
import { useFormPalette } from './DynamicFormPrimitives';
import CoverImageEditor from './CoverImageEditor';
import { coverHeightSx, formCardOverCoverSx } from './formCoverLayout';
import { getFormCoverGradient } from './formCoverGradient';
import './FormDesignShell.css';

/**
 * Public-form chrome for the template editor: cover band + centered card,
 * editable title/description. Matches PublicFormShell compact layout.
 */
export default function FormDesignShell({
  title,
  description,
  onTitleChange,
  onDescriptionChange,
  coverImageUrl,
  onCoverImageChange,
  formId,
  availablePlaceholders,
  children,
}) {
  const palette = useFormPalette();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const hasCustomCover = Boolean(coverImageUrl);
  const coverBackground = hasCustomCover
    ? `url(${coverImageUrl}) center / cover no-repeat`
    : getFormCoverGradient(formId, { isDark });

  const [coverHovered, setCoverHovered] = useState(false);
  const [coverAnchor, setCoverAnchor] = useState(null);
  const coverPopoverOpen = Boolean(coverAnchor);
  const showCoverControls = onCoverImageChange && (coverHovered || coverPopoverOpen);

  const handleCoverMouseEnter = useCallback(() => setCoverHovered(true), []);
  const handleCoverMouseLeave = useCallback(() => {
    if (!coverPopoverOpen) setCoverHovered(false);
  }, [coverPopoverOpen]);

  const openCoverPopover = useCallback((event) => {
    setCoverAnchor(event.currentTarget);
  }, []);

  const closeCoverPopover = useCallback(() => {
    setCoverAnchor(null);
    setCoverHovered(false);
  }, []);

  return (
    <Box className="form-design-shell" sx={{ bgcolor: palette.pageBg }}>
      <Box
        className="form-design-shell__cover-wrap"
        onMouseEnter={handleCoverMouseEnter}
        onMouseLeave={handleCoverMouseLeave}
      >
        <Box
          className="form-design-shell__cover"
          sx={{
            height: coverHeightSx,
            background: coverBackground,
          }}
        />
        {showCoverControls && (
          <>
            <Box className="form-design-shell__cover-scrim" aria-hidden="true" />
            <Box className="form-design-shell__cover-actions">
              <Button
                type="button"
                className="form-design-shell__cover-btn"
                variant="contained"
                size="small"
                startIcon={<ImageOutlinedIcon />}
                onClick={openCoverPopover}
              >
                {hasCustomCover ? 'Change cover' : 'Add cover image'}
              </Button>
            </Box>
          </>
        )}
        <Popover
          open={coverPopoverOpen}
          anchorEl={coverAnchor}
          onClose={closeCoverPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              className: 'form-design-shell__cover-popover',
              sx: {
                mt: 1,
                borderRadius: '12px',
                border: `1px solid ${palette.border}`,
                bgcolor: palette.surface,
                boxShadow: palette.shadow,
              },
            },
          }}
        >
          <CoverImageEditor
            className="cover-image-editor--popover"
            value={coverImageUrl || ''}
            onChange={onCoverImageChange}
          />
        </Popover>
      </Box>
      <Box
        className="form-design-shell__card"
        sx={{
          ...formCardOverCoverSx(palette),
        }}
      >
        <Box className="form-design-shell__header" sx={{ mb: 4 }}>
          <Box
            className="form-design-shell__title"
            sx={{ color: palette.textPrimary }}
          >
            <TipTapEditor
              value={title || ''}
              onChange={onTitleChange}
              showToolbar={false}
              stripHtml={true}
              availablePlaceholders={availablePlaceholders}
              placeholder="Form title"
              minHeight="auto"
              className="form-design-shell__title-editor tiptap-inline"
            />
          </Box>
          <Box
            className="form-design-shell__description"
            sx={{ color: palette.textSecondary }}
          >
            <TipTapEditor
              value={description || ''}
              onChange={onDescriptionChange}
              showToolbar={false}
              stripHtml={true}
              availablePlaceholders={availablePlaceholders}
              placeholder="Form description (optional)"
              minHeight="auto"
              className="form-design-shell__description-editor tiptap-inline"
            />
          </Box>
        </Box>

        <Box className="form-design-shell__fields">
          {children}
        </Box>

        <Typography
          className="form-design-shell__submit-hint"
          sx={{ color: palette.textSecondary }}
        >
          Submit button appears on the published form
        </Typography>
      </Box>
    </Box>
  );
}
